import { FC, useState, useCallback } from 'react';
import { Button, Card, MonthPicker } from '@features/ui';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, startOfMonth } from 'date-fns';
import { TReportPANewRenewalResetByMonthData } from '@src/types';
import { getPANewRenewalResetByMonth } from '@src/services';

interface PANewRenewalResetData extends TReportPANewRenewalResetByMonthData {
  values?: string;
}

type PANewRenewalResetPayload = {
  value: number;
  values?: string;
  payload?: PANewRenewalResetData;
  color?: string;
  dataKey?: string;
};

interface TooltipProps {
  active?: boolean;
  payload?: PANewRenewalResetPayload[];
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;

    return (
      <div
        className="bg-card-stat p-3 rounded-lg text-xs border border-[#3D3D3D] flex flex-col min-w-[200px] gap-y-2"
        style={{
          boxShadow: '0px 7px 13px 0px #B6D72C14',
        }}
      >
        <p className="text-white text-sm font-semibold text-center mb-1">
          {data?.package}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-[#999999] text-xs">{entry.dataKey}</span>
            </div>
            <span className="text-white text-sm font-medium">
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

interface CustomLegendProps {
  payload?: PANewRenewalResetPayload[];
}

const CustomLegend: FC<CustomLegendProps> = ({ payload }) => {
  if (!payload) return null;

  const legendData = [
    { label: 'New Accounts', color: '#B6D72C' },
    { label: 'Renewals', color: '#4A90E2' },
    { label: 'Resets', color: '#E25C5C' },
  ];

  return (
    <div className="flex justify-between items-center mb-5">
      <p className="text-2xl">PA New Renewal Reset By Month</p>
      <div className="flex gap-x-6 p-2.5">
        {legendData.map((entry, index) => (
          <div key={index} className="flex items-center gap-x-2">
            <div
              className="w-9 h-3 rounded-sm"
              style={{
                backgroundColor: entry.color,
              }}
            />
            <p className="text-sm">{entry.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PANewRenewalResetByMonth: FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(
    startOfMonth(new Date())
  );
  const [data, setData] = useState<PANewRenewalResetData[]>([]);
  const [totalAccounts, setTotalAccounts] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleGetData = useCallback(async () => {
    if (!selectedMonth) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getPANewRenewalResetByMonth({
        month: format(selectedMonth, 'yyyy-MM'),
      });

      // Transform API response to chart format
      const transformedData: PANewRenewalResetData[] = response.labels.map(
        (label: string, index: number) => ({
          package: label,
          new_accounts: response.new_accounts[index] || 0,
          renewals: response.renewals[index] || 0,
          resets: response.resets[index] || 0,
        })
      );

      setData(transformedData);

      // Use totals from API response
      const total =
        response.new_total + response.renewal_total + response.reset_total;

      setTotalAccounts(total);
    } catch (err) {
      console.error('Error fetching PA New/Renewal/Reset data:', err);
      setError('Failed to load report data. Please try again.');
      setData([]);
      setTotalAccounts(0);
    } finally {
      setIsLoading(false);
    }
  }, [selectedMonth]);

  return (
    <div>
      <div className="flex justify-between lg:items-center gap-7.5 lg:flex-row flex-col mb-6">
        <div className="text-xs flex lg:flex-row flex-col gap-x-8 gap-y-3">
          <h1 className="text-[23px]">
            New PA Accounts by Month {selectedMonth && '> '}
            {selectedMonth && format(selectedMonth, 'MMM yyyy')}
            {totalAccounts > 0 && ` (Total Accounts #${totalAccounts})`}
          </h1>
        </div>
      </div>

      <Card className="mt-4.5 pb-10">
        <div className="flex lg:flex-row flex-col justify-between items-start mb-5 gap-6">
          <div className="w-full flex gap-6 mb-5">
            <div className="flex flex-col gap-2">
              <label className="text-xs text-[#919EAB]" htmlFor="month-select">
                Month
              </label>
              <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
            </div>
          </div>
          <div className="flex gap-5">
            <Button
              className="bg-[#B6D72C] text-black hover:bg-[#9fb824] px-6"
              disabled={isLoading || !selectedMonth}
              onClick={handleGetData}
            >
              {isLoading ? 'Loading...' : 'Run Report'}
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded text-red-500 text-sm">
            {error}
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-[#919EAB]">Loading report data...</div>
          </div>
        )}

        {!isLoading && data.length === 0 && !error && (
          <div className="flex items-center justify-center py-20">
            <div className="text-[#919EAB]">
              No data available. Please select a month and run the report.
            </div>
          </div>
        )}

        {!isLoading && data.length > 0 && (
          <ResponsiveContainer height={770} width="100%">
            <BarChart
              data={data}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 30,
              }}
            >
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Legend
                align="center"
                content={<CustomLegend />}
                layout="horizontal"
                verticalAlign="top"
              />
              <CartesianGrid stroke="#919EAB3D" vertical={false} />
              <XAxis
                angle={-45}
                dataKey="package"
                dy={10}
                fontSize={11}
                height={200}
                interval={0}
                padding={{ left: 10, right: 10 }}
                stroke="#919EAB"
                textAnchor="end"
                tickLine={false}
              />
              <YAxis
                fontSize={12}
                label={{
                  value: 'NO. OF ACCOUNTS',
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' },
                }}
                stroke="#919EAB"
                tickLine={false}
              />
              <Bar
                barSize={19}
                dataKey="new_accounts"
                fill="#B6D72C"
                stackId="a"
              />
              <Bar barSize={19} dataKey="renewals" fill="#4A90E2" stackId="a" />
              <Bar barSize={19} dataKey="resets" fill="#E25C5C" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
};
